// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Badge } from "@mui/material";
import Icon from "@mui/material/Icon";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";

//  React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseIcon,
  collapseIconBox,
  collapseItem,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

//  React context
import { useTheme } from "@emotion/react";
import { useMaterialUIController } from "context";
function SidenavCollapse({ icon, name, active, ...rest }) {
  const [controller] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;

  const { palette, functions } = useTheme();
  const { primary, white, secondary, warning } = palette;

  const Content = (
    <MDBox
      {...rest}
      sx={(theme) =>
        collapseItem(theme, {
          active,
          miniSidenav,
          transparentSidenav,
          whiteSidenav,
          darkMode,
          sidenavColor,
        })
      }
    >
      <ListItemIcon
        sx={(theme) =>
          collapseIconBox(theme, {
            transparentSidenav,
            whiteSidenav,
            darkMode,
            active,
          })
        }
        className="sidenav-item-icon"
      >
        <MDBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          color={"inherit"}
        >
          {typeof icon === "string" ? (
            <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
          ) : typeof icon === "function" ? (
            <span style={{ fontSize: "1.3em", marginTop: "5px" }}>
              {icon(active ? primary.main : white.main)}
            </span>
          ) : (
            <Badge content={2} color="white">
              {icon}
            </Badge>
          )}
        </MDBox>
      </ListItemIcon>

      <ListItemText
        primary={name}
        sx={(theme) =>
          collapseText(theme, {
            miniSidenav,
            transparentSidenav,
            whiteSidenav,
            active,
          })
        }
      />
    </MDBox>
  );

  return (
    <ListItem component="li">
      {miniSidenav ? (
        <Tooltip
          title={name}
          placement="right"
          arrow
          PopperProps={{
            sx: {
              "& .MuiTooltip-tooltip": {
                color: darkMode ? white.main : primary.main,
                fontWeight: "bold",
                background: darkMode ? primary.main : white.main,
                borderRadius: "0.375rem",
                boxShadow: "0 5px 10px 0 rgba(0,0,0,0.1)",
                border: `2px solid ${palette.primary.main}`,
              },
              "& .MuiTooltip-arrow": {
                color: darkMode ? primary.main : white.main,
                "&:before": {
                  boxShadow: `2px 1px 0 ${palette.primary.main}`,
                  border: `2px solid ${palette.primary.main}`,
                },
              },
            },
          }}
        >
          {Content}
        </Tooltip>
      ) : (
        Content
      )}
    </ListItem>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
};

export default SidenavCollapse;
